import React from "react";

interface SlideContent {
  title?: string;
  subtitle?: string;
  exitType?: string;
  exitNote?: string;
  mainEmoji?: string;
  sideEmojis?: string[];
  footerText?: string;
  footerEmoji?: string;
  bgColor?: string;
  showSideDecoration?: boolean;
  showFooterDecoration?: boolean;
  showHeaderDecoration?: boolean;
  onEdit?: (content: SlideContent) => void;
}

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const ExitSlide2 = {
  id: "exit-slide2",
  name: "Exit Slide Style 2",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "exitType",
      label: "Exit Type",
      type: "text",
      defaultValue: "",
    },
    {
      id: "exitNote",
      label: "Exit Note",
      type: "text",
      defaultValue: "",
    },
    {
      id: "mainEmoji",
      label: "Main Emoji",
      type: "text",
      defaultValue: "",
    },
    {
      id: "sideEmojis",
      label: "Side Emojis (Array)",
      type: "text",
      defaultValue: JSON.stringify(["", "", "", ""]),
    },
    {
      id: "footerText",
      label: "Footer Text",
      type: "text",
      defaultValue: "",
    },
    {
      id: "footerEmoji",
      label: "Footer Emoji",
      type: "text",
      defaultValue: "",
    },
    {
      id: "showSideDecoration",
      label: "Show Side Decoration",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "showFooterDecoration",
      label: "Show Footer Decoration",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "showHeaderDecoration",
      label: "Show Header Decoration",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      exitType = "",
      exitNote = "",
      mainEmoji = "",
      sideEmojis = ["", "", "", ""],
      footerText = "",
      footerEmoji = "",
      showSideDecoration = true,
      showFooterDecoration = true,
      showHeaderDecoration = true,
      bgColor = "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleSideEmojiEdit = (index: number) => (value: string) => {
      const newSideEmojis = [...sideEmojis];
      newSideEmojis[index] = value;
      handleEdit("sideEmojis")(JSON.stringify(newSideEmojis));
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Side Decorations */}
        {showSideDecoration && (
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white/5 to-transparent" />
        )}
        {showSideDecoration && (
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white/5 to-transparent" />
        )}

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-12">
          {/* Header */}
          <div className="text-center mb-12">
            {showHeaderDecoration && (
              <div className="flex justify-center gap-4 mb-4">
                {sideEmojis.slice(0, 2).map((emoji, idx) => (
                  <EditableDiv
                    key={idx}
                    text={emoji}
                    onEdit={handleSideEmojiEdit(idx)}
                    className="text-3xl transform hover:scale-110 transition-transform"
                  />
                ))}
              </div>
            )}
            <EditableDiv
              text={title}
              onEdit={handleEdit("title")}
              className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-blue-100"
            />
            <EditableDiv
              text={subtitle}
              onEdit={handleEdit("subtitle")}
              className="text-xl text-blue-100/80"
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="text-6xl mb-8 transform hover:scale-110 transition-transform">
              <EditableDiv
                text={mainEmoji}
                onEdit={handleEdit("mainEmoji")}
                className=""
              />
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl w-full mb-8">
              <div className="text-center mb-4">
                <EditableDiv
                  text={exitType}
                  onEdit={handleEdit("exitType")}
                  className="text-2xl font-bold text-blue-100"
                />
              </div>
              <EditableDiv
                text={exitNote}
                onEdit={handleEdit("exitNote")}
                className="text-lg text-blue-100/90 text-center leading-relaxed"
              />
            </div>

            {showSideDecoration && (
              <div className="flex justify-center gap-8 mb-8">
                {sideEmojis.slice(2, 4).map((emoji, idx) => (
                  <EditableDiv
                    key={idx}
                    text={emoji}
                    onEdit={handleSideEmojiEdit(idx + 2)}
                    className="text-3xl transform hover:scale-110 transition-transform"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {showFooterDecoration && (
            <div className="text-center">
              <EditableDiv
                text={footerEmoji}
                onEdit={handleEdit("footerEmoji")}
                className="text-3xl mb-2"
              />
              <EditableDiv
                text={footerText}
                onEdit={handleEdit("footerText")}
                className="text-sm text-blue-100/70 italic"
              />
            </div>
          )}
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default ExitSlide2;
